import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';

const repoCollectionEndpoint = apiUrl + 'agent-insights/repo-collection/';

export interface AgentInsightsRepoCollectionPayload {
  orgUUID: string;
  /** Repos to set explicitly. Omit to change only the org default. */
  repoNames?: string[];
  /** Required when `repoNames` is non-empty. */
  enabled?: boolean;
  /**
   * Org-level default applied to every repo with no explicit setting. This is
   * the only way to express an intent about repos that don't exist yet — a
   * per-repo setting can't cover a repo nobody has created.
   */
  orgDefault?: boolean;
}

export interface AgentInsightsRepoCollectionResponse {
  success: boolean;
  /** How many repo rows changed. Fewer than requested ⇒ some names weren't found. */
  updated?: number;
  /** The org default in force after the call — render inheriting rows from this. */
  org_default?: boolean;
  error?: string | null;
  code?: string | null;
}

/**
 * Set which repositories collect agent insights, and/or the org-level default.
 *
 * Takes a batch so "search a prefix → select all → toggle" is one request
 * rather than one per repo. Admin-only server-side.
 */
export const setAgentInsightsRepoCollection = async (
  payload: AgentInsightsRepoCollectionPayload
): Promise<AgentInsightsRepoCollectionResponse> => {
  try {
    const body: Record<string, unknown> = { OrgUUID: payload.orgUUID };
    if (payload.repoNames?.length) {
      body.RepoNames = payload.repoNames;
      body.Enabled = payload.enabled;
    }
    if (payload.orgDefault !== undefined) {
      body.OrgDefault = payload.orgDefault;
    }

    const response = await postWithAuth(repoCollectionEndpoint, body);

    if (!response.ok) {
      throw new Error('Failed to update agent insights collection');
    }

    const data = await response.json();
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;

    if (parsed?.error) {
      throw new Error(parsed.error);
    }

    return parsed;
  } catch (error) {
    console.error(error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update agent insights collection',
    };
  }
};
